import { useState, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Copy, Check, FileDown, FileCode2, ChevronDown, ChevronRight, Sparkles, ExternalLink, FileText } from 'lucide-react';
// Single source of truth for the rich blog fragment renderer (moved out of this
// file so it can be reused at approve/schedule time without mounting the viewer).
import { escapeHtml, paragraphsToHtml, buildCleanHtml } from './cleanHtml';
import ViewerToolbar from '../common/ViewerToolbar';

// ─────────────────────────── Labels ───────────────────────────
const LABELS = {
  de: { toc: 'Inhaltsverzeichnis', takeaways: 'Das Wichtigste in Kürze', conclusion: 'Fazit', faq: 'Häufig gestellte Fragen',
    related: 'Weiterführende Artikel', related_line: 'Weiterführende Artikel werden hier angezeigt, sobald weitere Beiträge veröffentlicht sind.',
    cta_headline: 'Bereit, Ihre Content-Strategie zu automatisieren?', cta_button: 'Demo buchen',
    meta_section: 'SEO & Meta-Informationen', reading_time: 'min Lesezeit',
    export_pdf: 'PDF exportieren', export_html: 'HTML exportieren', preview: 'Vorschau', html_view: 'HTML',
    copy: 'Kopieren', copied: 'Kopiert!', by: 'Von',
    meta_title_lbl: 'Meta Titel', meta_desc_lbl: 'Meta Beschreibung', slug_lbl: 'Slug', keyword_lbl: 'Hauptkeyword',
    image_filename_lbl: 'Bilddatei (Empfehlung)', image_alt_lbl: 'Alt-Text (Empfehlung)',
    save: 'Änderungen speichern', saving: 'Speichern…', saved: 'Gespeichert',
    words: 'Wörter', chars: 'Zeichen', total: 'Gesamt' },
  en: { toc: 'Table of Contents', takeaways: 'Key Takeaways', conclusion: 'Conclusion', faq: 'Frequently Asked Questions',
    related: 'Related Articles', related_line: 'Related articles will appear here once more posts are published.',
    cta_headline: 'Ready to automate your content strategy?', cta_button: 'Book a demo',
    meta_section: 'SEO & Meta Information', reading_time: 'min read',
    export_pdf: 'Export PDF', export_html: 'Export HTML', preview: 'Preview', html_view: 'HTML',
    copy: 'Copy', copied: 'Copied!', by: 'By',
    meta_title_lbl: 'Meta Title', meta_desc_lbl: 'Meta Description', slug_lbl: 'Slug', keyword_lbl: 'Main keyword',
    image_filename_lbl: 'Image filename', image_alt_lbl: 'Alt text',
    save: 'Save changes', saving: 'Saving…', saved: 'Saved',
    words: 'words', chars: 'chars', total: 'Total' },
};
// Other languages fall back to EN for label keys
['fr','es','it','nl','pl','pt'].forEach(l => { LABELS[l] = LABELS.en; });

// Search-intent labels — displayed as a small badge above the article so the
// editor can verify the pipeline classified the topic correctly.
const INTENT_LABELS = {
  de: {
    comparison:    { label: 'Vergleich',         color: 'amber'  },
    definition:    { label: 'Definition',        color: 'blue'   },
    guide:         { label: 'Guide',             color: 'violet' },
    howto:         { label: 'How-To',            color: 'emerald'},
    commercial:    { label: 'Kaufberatung',      color: 'rose'   },
    category:      { label: 'Übersicht',         color: 'cyan'   },
    informational: { label: 'Informational',     color: 'slate'  },
  },
  en: {
    comparison:    { label: 'Comparison',           color: 'amber'  },
    definition:    { label: 'Definition',           color: 'blue'   },
    guide:         { label: 'Guide',                color: 'violet' },
    howto:         { label: 'How-To',               color: 'emerald'},
    commercial:    { label: 'Commercial',           color: 'rose'   },
    category:      { label: 'Category Overview',    color: 'cyan'   },
    informational: { label: 'Informational',        color: 'slate'  },
  },
};
const INTENT_BADGE_CLASSES = {
  amber:   'bg-amber-50 text-amber-700 ring-amber-200',
  blue:    'bg-blue-50 text-blue-700 ring-blue-200',
  violet:  'bg-violet-50 text-violet-700 ring-violet-200',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  rose:    'bg-rose-50 text-rose-700 ring-rose-200',
  cyan:    'bg-cyan-50 text-cyan-700 ring-cyan-200',
  slate:   'bg-slate-100 text-slate-700 ring-slate-200',
};

// Read section title/body tolerantly — backend variants: h2/header/headline/heading + content/body/text
const secTitle = (s) => s?.h2 || s?.header || s?.headline || s?.heading || s?.title || '';
const secBody  = (s) => {
  const v = s?.content || s?.body || s?.text || '';
  return Array.isArray(v) ? v.filter(Boolean).join('\n\n') : v;
};

// Resolve the article H1 across all known shapes + fallback prop.
//   1) blogData.meta.title  2) blogData.title  3) content_piece.title  4) ''
// Also accepts meta.headline / meta.h1 as additional LLM variants.
const resolveTitle = (blogData, fallback) => {
  const meta = blogData?.meta || {};
  return (
    meta.title || meta.headline || meta.h1 ||
    blogData?.title || blogData?.headline || blogData?.h1 ||
    fallback || ''
  );
};

function detectLanguage(blogData, override) {
  if (override && LABELS[override]) return override;
  const sample = ((blogData?.intro || '') + ' ' + (blogData?.meta?.title || '')).toLowerCase().slice(0, 300);
  const deWords = ['der', 'die', 'das', 'und', 'ist', 'für', 'mit', 'von'];
  const matches = deWords.reduce((acc, w) => acc + (sample.includes(' ' + w + ' ') ? 1 : 0), 0);
  return matches >= 2 ? 'de' : 'en';
}

const countWords = (s) => (String(s || '').trim().match(/\S+/g) || []).length;
const countChars = (s) => String(s || '').length;

// ContentEditable wrapper — uses ref to avoid wiping user input on parent re-renders.
// innerHTML is only re-synced when the `value` prop changes from OUTSIDE
// (i.e. NOT from this component's own onBlur).
const Editable = ({ value, onChange, className = '', as: Tag = 'div', placeholder = '', testId, style }) => {
  const ref = useRef(null);
  const lastExternalValue = useRef(value);

  useEffect(() => {
    if (!ref.current) return;
    // Only sync DOM if external value has actually changed
    // (prevents wiping user input on every parent re-render)
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      const html = escapeHtml(value || '').replace(/\n/g, '<br>');
      if (ref.current.innerHTML !== html) {
        ref.current.innerHTML = html;
      }
    }
  }, [value]);

  // Initial mount only — set innerHTML once
  useEffect(() => {
    if (ref.current && ref.current.innerHTML === '') {
      ref.current.innerHTML = escapeHtml(value || '').replace(/\n/g, '<br>');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`${className} outline-none focus:ring-2 focus:ring-offset-2 rounded transition-shadow`}
      style={{ minHeight: '1.5em', ...style, '--tw-ring-color': '#1e9ead' }}
      data-placeholder={placeholder}
      data-testid={testId}
      onBlur={(e) => {
        const newText = e.currentTarget.innerText;
        lastExternalValue.current = newText;  // mark as our own change, prevent re-sync
        onChange(newText);
      }}
    />
  );
};

const WordCharCounter = ({ text, t }) => (
  <p className="text-xs text-gray-400 mt-1 no-print">
    {countWords(text)} {t.words} · {countChars(text)} {t.chars}
  </p>
);

// ─────────────────────────────────────────────────────────────
// Table parsing utilities — used by TableEditor and the regex extractor
// that finds every markdown table inside a section content string.
// A "table region" is { start, end, header[], rows[][] } where start/end
// are character offsets into the source string (so we can splice replacements).
// ─────────────────────────────────────────────────────────────
const TABLE_BLOCK_RE = /(^|\n)(\|[^\n]*\|\s*\n\|\s*[-:|\s]+\|\s*\n(?:\|[^\n]*\|\s*\n?)+)/g;

const parseTableBlock = (block) => {
  const lines = block.trim().split('\n');
  const cells = (row) => row.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());
  const header = cells(lines[0]);
  const rows = lines.slice(2).filter(l => /\|/.test(l)).map(cells);
  // pad rows so every row has header.length cells
  const cols = header.length;
  const padded = rows.map(r => {
    const out = r.slice(0, cols);
    while (out.length < cols) out.push('');
    return out;
  });
  return { header, rows: padded };
};

const serializeTableBlock = ({ header, rows }) => {
  const cols = header.length;
  const head = `| ${header.join(' | ')} |`;
  const sep  = `| ${header.map(() => '---').join(' | ')} |`;
  const body = rows.map(r => {
    const padded = r.slice(0, cols);
    while (padded.length < cols) padded.push('');
    return `| ${padded.join(' | ')} |`;
  }).join('\n');
  return `${head}\n${sep}${body ? '\n' + body : ''}`;
};

// Extract all table regions from a content string. Returns
// [{ start, end, parsed: {header, rows} }, …] sorted by start asc.
const extractTableRegions = (content) => {
  if (!content) return [];
  const regions = [];
  TABLE_BLOCK_RE.lastIndex = 0;
  let m;
  while ((m = TABLE_BLOCK_RE.exec(content)) !== null) {
    const leadingNewline = m[1] ? 1 : 0;
    const start = m.index + leadingNewline;
    const block = m[2];
    const end = start + block.length;
    regions.push({ start, end, raw: block, parsed: parseTableBlock(block) });
  }
  return regions;
};

// Replace the i-th table region in `content` with a new parsed table.
const replaceTableRegion = (content, regionIndex, newParsed) => {
  const regions = extractTableRegions(content);
  if (regionIndex < 0 || regionIndex >= regions.length) return content;
  const r = regions[regionIndex];
  const newBlock = serializeTableBlock(newParsed) + '\n';
  return content.slice(0, r.start) + newBlock + content.slice(r.end);
};

// ─────────────────────────────────────────────────────────────
// TeCell — uncontrolled contentEditable cell.
// React + contentEditable + JSX children = duplicated-text bug. We work around
// it by managing the DOM textContent imperatively via ref. React never
// re-renders the cell's inner content; only the parent decides via `value`
// changes (e.g. row/col added). We track our own last committed value so we
// only sync when the parent changed it externally.
// ─────────────────────────────────────────────────────────────
const TeCell = ({ value, onCommit, className, placeholder, testId }) => {
  const ref = useRef(null);
  const lastCommitted = useRef(value ?? '');

  // Initial mount: seed DOM once
  useEffect(() => {
    if (ref.current && ref.current.textContent !== (value ?? '')) {
      ref.current.textContent = value ?? '';
      lastCommitted.current = value ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External updates: only re-seed when the parent-provided value differs from
  // what we last committed (e.g. after "add row" resets a cell to '').
  useEffect(() => {
    if (!ref.current) return;
    const incoming = value ?? '';
    if (incoming === lastCommitted.current) return;
    ref.current.textContent = incoming;
    lastCommitted.current = incoming;
  }, [value]);

  return (
    <span
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={className}
      data-placeholder={placeholder}
      data-testid={testId}
      onBlur={(e) => {
        const v = (e.currentTarget.textContent || '').trim();
        lastCommitted.current = v;
        onCommit(v);
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────
// TableEditor — per-cell editable table.
// Renders a real <table> where every cell is a TeCell (uncontrolled).
// Header rows are bold-styled. Add/remove row/column controls live in the
// toolbar. Structural edits never break the markdown (delegated through
// serializeTableBlock).
// ─────────────────────────────────────────────────────────────
const TableEditor = ({ parsed, onChange, lang = 'de', testId }) => {
  const [data, setData] = useState(parsed);
  const lastParentSig = useRef(JSON.stringify(parsed));

  // Re-sync if parent passes a new parsed object (after external edits)
  useEffect(() => {
    const sig = JSON.stringify(parsed);
    if (sig !== lastParentSig.current) {
      lastParentSig.current = sig;
      setData(parsed);
    }
  }, [parsed]);

  const commit = (next) => {
    setData(next);
    lastParentSig.current = JSON.stringify(next);
    onChange(next);
  };

  const updateHeader = (ci, value) => {
    const header = [...data.header];
    header[ci] = value;
    commit({ ...data, header });
  };
  const updateCell = (ri, ci, value) => {
    const rows = data.rows.map((r, i) => i === ri ? r.map((c, j) => j === ci ? value : c) : r);
    commit({ ...data, rows });
  };
  const addRow = () => {
    const rows = [...data.rows, data.header.map(() => '')];
    commit({ ...data, rows });
  };
  const removeRow = (ri) => {
    const rows = data.rows.filter((_, i) => i !== ri);
    commit({ ...data, rows });
  };
  const addCol = () => {
    // Seed new headers with '' so the CSS ::before placeholder shows the label
    // instead of injecting literal text into the editable node (which would
    // require the user to Ctrl+A before typing).
    const header = [...data.header, ''];
    const rows = data.rows.map(r => [...r, '']);
    commit({ ...data, header, rows });
  };
  const removeCol = (ci) => {
    if (data.header.length <= 1) return;
    const header = data.header.filter((_, i) => i !== ci);
    const rows = data.rows.map(r => r.filter((_, i) => i !== ci));
    commit({ ...data, header, rows });
  };

  const L = lang === 'de'
    ? { addRow: '+ Zeile', addCol: '+ Spalte', delRow: 'Zeile löschen', delCol: 'Spalte löschen', empty: 'Klick zum Bearbeiten' }
    : { addRow: '+ Row',   addCol: '+ Column', delRow: 'Delete row',   delCol: 'Delete column',   empty: 'Click to edit' };

  return (
    <div className="table-editor" data-testid={testId}>
      <div className="te-toolbar no-print">
        <button type="button" onClick={addRow} className="te-btn" data-testid={`${testId}-add-row`}>{L.addRow}</button>
        <button type="button" onClick={addCol} className="te-btn" data-testid={`${testId}-add-col`}>{L.addCol}</button>
      </div>
      <div className="table-wrap">
        <table className="rich-table te-table">
          <thead>
            <tr>
              {data.header.map((h, ci) => (
                <th key={`h-${ci}-${data.header.length}`} scope="col">
                  <div className="te-th-inner">
                    <TeCell
                      value={h}
                      onCommit={(v) => updateHeader(ci, v)}
                      placeholder={L.empty}
                      testId={`${testId}-th-${ci}`}
                      className="te-cell te-th-cell"
                    />
                    <button
                      type="button"
                      onClick={() => removeCol(ci)}
                      className="te-cell-del no-print"
                      title={L.delCol}
                      aria-label={L.delCol}
                      data-testid={`${testId}-del-col-${ci}`}
                    >×</button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, ri) => (
              <tr key={`r-${ri}-${data.rows.length}-${data.header.length}`}>
                {row.map((cell, ci) => (
                  <td key={`c-${ri}-${ci}-${data.header.length}`}>
                    <div className="te-td-inner">
                      <TeCell
                        value={cell}
                        onCommit={(v) => updateCell(ri, ci, v)}
                        placeholder={L.empty}
                        testId={`${testId}-cell-${ri}-${ci}`}
                        className="te-cell"
                      />
                      {ci === row.length - 1 && (
                        <button
                          type="button"
                          onClick={() => removeRow(ri)}
                          className="te-row-del no-print"
                          title={L.delRow}
                          aria-label={L.delRow}
                          data-testid={`${testId}-del-row-${ri}`}
                        >×</button>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────
// BLOCK ARCHITECTURE — splitIntoBlocks / serializeBlocks
// The section content is stored as a single markdown string, but the editor
// works on a typed block array: paragraph | bullet_list | numbered_list |
// callout | table. Each block is edited independently — editing one never
// touches the raw markup of another.
// ─────────────────────────────────────────────────────────────
const CALLOUT_RE = /^\s*>\s*(INFO|EINORDNUNG|ZUSAMMENFASSUNG|SUMMARY|BOTTOM\s+LINE|FAZIT|WICHTIG|IMPORTANT|WARNING|ACHTUNG):/i;

let _blockIdSeq = 0;
const nextBlockId = () => `blk-${++_blockIdSeq}`;

const classifyBlock = (chunk) => {
  const trimmed = chunk.trim();
  if (!trimmed) return null;
  const lines = trimmed.split(/\n/);
  if (lines.length >= 2
      && /^\s*\|.*\|\s*$/.test(lines[0])
      && /^\s*\|?\s*[-:|\s]+\|?\s*$/.test(lines[1])) {
    return { type: 'table', parsed: parseTableBlock(trimmed) };
  }
  if (CALLOUT_RE.test(trimmed)) return { type: 'callout' };
  if (lines.every(l => l.trim() === '' || /^\s*\d+\.\s+/.test(l))
      && lines.some(l => /^\s*\d+\.\s+/.test(l))) {
    return { type: 'numbered_list' };
  }
  if (lines.every(l => l.trim() === '' || /^\s*-\s+/.test(l))
      && lines.some(l => /^\s*-\s+/.test(l))) {
    return { type: 'bullet_list' };
  }
  return { type: 'paragraph' };
};

const splitIntoBlocks = (content) => {
  if (!content || typeof content !== 'string') return [];
  return content.split(/\n\s*\n/).map(chunk => {
    const t = chunk.trim();
    if (!t) return null;
    const cls = classifyBlock(t);
    if (!cls) return null;
    return { id: nextBlockId(), type: cls.type, raw: t, parsed: cls.parsed };
  }).filter(Boolean);
};

const serializeBlocks = (blocks) => (blocks || []).map(b => {
  if (b.type === 'table' && b.parsed) return serializeTableBlock(b.parsed);
  return b.raw || '';
}).filter(Boolean).join('\n\n');

const singleBlockToHtml = (block) => {
  if (!block) return '';
  if (block.type === 'table' && block.parsed) {
    return paragraphsToHtml(serializeTableBlock(block.parsed));
  }
  return paragraphsToHtml(block.raw || '');
};

// ─────────────────────────────────────────────────────────────
// IN-PLACE BLOCK EDITORS — one component per block type.
// Every component follows the same principle as Editable / TeCell: a permanent
// contentEditable node. No mode toggle. No textarea swap. Zero layout shift
// when the caret enters. Users see the finished rendering and edit in-place.
// ─────────────────────────────────────────────────────────────

// Shared low-level: a contentEditable node whose text is managed imperatively
// via ref so React never re-renders the inner content (avoids duplicated-text
// bugs). Accepts multi-line input; newlines preserved as \n on commit.
const InPlaceTextNode = ({ value, onCommit, as: Tag = 'div', className, style, placeholder, testId, allowMultiline = true, onEmptyBlur }) => {
  const ref = useRef(null);
  const lastCommitted = useRef(value ?? '');

  const seed = (v) => {
    if (!ref.current) return;
    const html = escapeHtml(v || '').replace(/\n/g, '<br>');
    if (ref.current.innerHTML !== html) ref.current.innerHTML = html;
  };

  useEffect(() => { seed(value); lastCommitted.current = value ?? ''; /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    const incoming = value ?? '';
    if (incoming !== lastCommitted.current) {
      seed(incoming);
      lastCommitted.current = incoming;
    }
  }, [value]);

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className={`${className || ''} outline-none focus:ring-2 focus:ring-[#1e9ead]/40 rounded transition-shadow`}
      style={{ minHeight: '1.2em', ...style }}
      data-placeholder={placeholder || ''}
      data-testid={testId}
      onKeyDown={(e) => {
        if (!allowMultiline && e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
      }}
      onBlur={(e) => {
        const v = e.currentTarget.innerText.replace(/\u00A0/g, ' ');
        lastCommitted.current = v;
        const trimmed = v.trim();
        if (trimmed === '' && onEmptyBlur) { onEmptyBlur(); return; }
        onCommit(v);
      }}
    />
  );
};

// ── Paragraph — no markers, just multi-line prose ─────────────
const InPlaceParagraph = ({ block, onChange, onDelete, testId }) => (
  <p className="block-view block-type-paragraph rich-p">
    <InPlaceTextNode
      as="span"
      value={block.raw}
      onCommit={(v) => onChange({ ...block, raw: v })}
      onEmptyBlur={onDelete}
      testId={testId}
      className="block"
    />
  </p>
);

// ── Bullet list — one editable item per <li> ──────────────────
const parseListItems = (raw, ordered) =>
  (raw || '').split(/\n/).map(l => l.replace(ordered ? /^\s*\d+\.\s+/ : /^\s*-\s+/, '').trimEnd()).filter(x => x.length > 0);
const serializeBulletItems = (items) => items.map(i => `- ${i}`).join('\n');
const serializeNumberedItems = (items) => items.map((i, idx) => `${idx + 1}. ${i}`).join('\n');

const InPlaceBulletList = ({ block, onChange, onDelete, lang = 'de', testId }) => {
  const [items, setItems] = useState(() => parseListItems(block.raw, false));
  const lastCommitted = useRef(block.raw);

  useEffect(() => {
    if (block.raw !== lastCommitted.current) {
      setItems(parseListItems(block.raw, false));
      lastCommitted.current = block.raw;
    }
  }, [block.raw]);

  const commitToParent = (next) => {
    const cleaned = next.filter(x => x.trim().length > 0);
    if (cleaned.length === 0) { onDelete(); return; }
    const newRaw = serializeBulletItems(cleaned);
    lastCommitted.current = newRaw;
    onChange({ ...block, raw: newRaw });
  };

  const updateItem = (idx, value) => {
    const next = items.map((it, i) => i === idx ? value : it);
    setItems(next);
    commitToParent(next);
  };
  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    commitToParent(next);
  };
  const addItem = () => setItems([...items, '']);

  return (
    <ul className="rich-bullets block-view block-type-bullet_list" data-testid={testId}>
      {items.map((it, i) => (
        <li key={`b-${block.id}-${i}`} className="rich-bullet-li group">
          <InPlaceTextNode
            as="span"
            value={it}
            onCommit={(v) => updateItem(i, v)}
            onEmptyBlur={() => removeItem(i)}
            allowMultiline={false}
            testId={`${testId}-item-${i}`}
            className="flex-1"
            placeholder={lang === 'de' ? 'Punkt schreiben…' : 'Type item…'}
          />
        </li>
      ))}
      <li className="rich-bullet-add no-print">
        <button type="button" onClick={addItem} className="text-xs font-medium text-[#1e9ead] hover:text-[#0d7278]" data-testid={`${testId}-add-item`}>
          {lang === 'de' ? '+ Punkt hinzufügen' : '+ Add item'}
        </button>
      </li>
    </ul>
  );
};

// ── Numbered list — same, but stepnum badge + ordered serialization ──
const InPlaceNumberedList = ({ block, onChange, onDelete, lang = 'de', testId }) => {
  const [items, setItems] = useState(() => parseListItems(block.raw, true));
  const lastCommitted = useRef(block.raw);

  useEffect(() => {
    if (block.raw !== lastCommitted.current) {
      setItems(parseListItems(block.raw, true));
      lastCommitted.current = block.raw;
    }
  }, [block.raw]);

  const commitToParent = (next) => {
    const cleaned = next.filter(x => x.trim().length > 0);
    if (cleaned.length === 0) { onDelete(); return; }
    const newRaw = serializeNumberedItems(cleaned);
    lastCommitted.current = newRaw;
    onChange({ ...block, raw: newRaw });
  };

  const updateItem = (idx, value) => {
    const next = items.map((it, i) => i === idx ? value : it);
    setItems(next);
    commitToParent(next);
  };
  const removeItem = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    commitToParent(next);
  };
  const addItem = () => setItems([...items, '']);

  return (
    <ol className="rich-steps block-view block-type-numbered_list" data-testid={testId}>
      {items.map((it, i) => (
        <li key={`n-${block.id}-${i}`} className="group">
          <span className="step-num">{i + 1}</span>
          <InPlaceTextNode
            as="span"
            value={it}
            onCommit={(v) => updateItem(i, v)}
            onEmptyBlur={() => removeItem(i)}
            allowMultiline={false}
            testId={`${testId}-item-${i}`}
            className="step-text flex-1"
            placeholder={lang === 'de' ? 'Schritt schreiben…' : 'Type step…'}
          />
        </li>
      ))}
      <li className="rich-step-add no-print" style={{ background: 'transparent', border: 0, padding: 0 }}>
        <button type="button" onClick={addItem} className="text-xs font-medium text-[#1e9ead] hover:text-[#0d7278]" data-testid={`${testId}-add-item`}>
          {lang === 'de' ? '+ Schritt hinzufügen' : '+ Add step'}
        </button>
      </li>
    </ol>
  );
};

// ── Callout — icon + type dropdown + editable body ─────────────
const CALLOUT_TYPES = [
  { key: 'INFO',              cls: 'callout-info',        label: 'Info' },
  { key: 'EINORDNUNG',        cls: 'callout-einordnung',  label: 'Einordnung' },
  { key: 'ZUSAMMENFASSUNG',   cls: 'callout-summary',     label: 'Zusammenfassung' },
  { key: 'WICHTIG',           cls: 'callout-important',   label: 'Wichtig' },
];
const CALLOUT_LEGACY_MAP = { SUMMARY: 'ZUSAMMENFASSUNG', 'BOTTOM LINE': 'ZUSAMMENFASSUNG', FAZIT: 'ZUSAMMENFASSUNG', IMPORTANT: 'WICHTIG', WARNING: 'WICHTIG', ACHTUNG: 'WICHTIG' };

const parseCalloutBlock = (raw) => {
  const m = (raw || '').match(/^\s*>\s*(INFO|EINORDNUNG|ZUSAMMENFASSUNG|SUMMARY|BOTTOM\s+LINE|FAZIT|WICHTIG|IMPORTANT|WARNING|ACHTUNG):\s*/i);
  if (!m) return { type: 'INFO', body: (raw || '').replace(/^>\s?/gm, '').trim() };
  const rawType = m[1].toUpperCase().replace(/\s+/g, ' ');
  const type = CALLOUT_LEGACY_MAP[rawType] || rawType;
  const body = raw.slice(m[0].length).replace(/\n>\s?/g, '\n').trim();
  return { type, body };
};

const InPlaceCallout = ({ block, onChange, onDelete, testId }) => {
  const { type, body } = parseCalloutBlock(block.raw);
  const meta = CALLOUT_TYPES.find(t => t.key === type) || CALLOUT_TYPES[0];

  const commitType = (nextType) => {
    onChange({ ...block, raw: `> ${nextType}: ${body}` });
  };
  const commitBody = (nextBody) => {
    if (!nextBody.trim()) { onDelete(); return; }
    onChange({ ...block, raw: `> ${type}: ${nextBody}` });
  };

  return (
    <aside className={`callout ${meta.cls} block-view block-type-callout group`} role="note" data-testid={testId} data-callout-type={type}>
      <div className="callout-icon" aria-hidden="true">
        <CalloutIcon type={type} />
      </div>
      <div className="callout-body flex-1 min-w-0">
        <div className="callout-header flex items-center justify-between gap-2 mb-1 no-print">
          <select
            value={type}
            onChange={(e) => commitType(e.target.value)}
            className="text-[10px] uppercase tracking-wider font-bold bg-transparent border border-current/20 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-current/50"
            data-testid={`${testId}-type-select`}
          >
            {CALLOUT_TYPES.map(t => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-[10px] font-medium text-current/60 hover:text-rose-600 transition-opacity"
            title="Callout löschen"
            aria-label="Callout löschen"
            data-testid={`${testId}-delete`}
          >×</button>
        </div>
        <div className="callout-label print-only">{meta.label}</div>
        <InPlaceTextNode
          as="div"
          value={body}
          onCommit={commitBody}
          onEmptyBlur={onDelete}
          testId={`${testId}-body`}
          className="callout-text"
        />
      </div>
    </aside>
  );
};

// Small SVG dispatcher for callout icons — mirrors paragraphsToHtml's inline SVGs
const CalloutIcon = ({ type }) => {
  if (type === 'EINORDNUNG') return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z" /><path d="M12 8v8" /><path d="M8 12h8" /></svg>
  );
  if (type === 'ZUSAMMENFASSUNG') return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></svg>
  );
  if (type === 'WICHTIG') return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
  );
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
  );
};

// ─────────────────────────────────────────────────────────────
// TextBlockEditor — dispatcher. Routes by block.type. Every branch is an
// in-place editor — same UX as Editable/TeCell. No mode toggle. No textarea.
// ─────────────────────────────────────────────────────────────
const TextBlockEditor = ({ block, onChange, onDelete, lang = 'de', testId }) => {
  const common = { block, onChange, onDelete, lang, testId };
  if (block.type === 'callout')       return <InPlaceCallout       {...common} />;
  if (block.type === 'bullet_list')   return <InPlaceBulletList    {...common} />;
  if (block.type === 'numbered_list') return <InPlaceNumberedList  {...common} />;
  return <InPlaceParagraph {...common} />;
};




// ─────────────────────────────────────────────────────────────
// RichEditable — pure block-based editor.
// Parses the value once into a block array; each block is edited in isolation
// via TextBlockEditor (for prose/lists/callouts) or TableEditor (for tables).
// Editing one block never touches the raw markup of another block. When the
// last block is empty (e.g. after delete), we show a "click to add" prompt.
// ─────────────────────────────────────────────────────────────
const RichEditable = ({ value, onChange, testId, placeholder, lang = 'de' }) => {
  const [blocks, setBlocks] = useState(() => splitIntoBlocks(value || ''));
  const lastSerialisedByUs = useRef(serializeBlocks(splitIntoBlocks(value || '')));
  const [adding, setAdding] = useState(false);
  const addRef = useRef(null);

  // Re-parse only when the parent's value changed from an EXTERNAL source
  // (e.g. brand-agent regeneration). We compare against our own last serialised
  // output so we don't stomp on cell-level edits mid-flight.
  useEffect(() => {
    const incoming = value || '';
    if (incoming === lastSerialisedByUs.current) return;
    setBlocks(splitIntoBlocks(incoming));
    lastSerialisedByUs.current = incoming;
  }, [value]);

  useEffect(() => {
    if (adding && addRef.current) addRef.current.focus();
  }, [adding]);

  const commit = (nextBlocks) => {
    setBlocks(nextBlocks);
    const serialised = serializeBlocks(nextBlocks);
    lastSerialisedByUs.current = serialised;
    onChange(serialised);
  };

  const updateBlock = (idx, next) => commit(blocks.map((b, i) => i === idx ? next : b));
  const deleteBlock = (idx) => commit(blocks.filter((_, i) => i !== idx));
  const appendFromText = (text) => {
    if (!text || !text.trim()) return;
    const newBlocks = splitIntoBlocks(text.trim());
    if (!newBlocks.length) return;
    commit([...blocks, ...newBlocks]);
  };

  const L = lang === 'de'
    ? { addBlock: '+ Textblock hinzufügen', empty: placeholder || 'Hier klicken zum Bearbeiten…' }
    : { addBlock: '+ Add text block',        empty: placeholder || 'Click to edit…' };

  if (blocks.length === 0 && !adding) {
    return (
      <div className="rich-content" data-testid={testId}>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="text-left text-gray-400 italic hover:text-gray-600 transition-colors no-print"
          data-testid={`${testId}-empty-add`}
        >{L.empty}</button>
      </div>
    );
  }

  return (
    <div className="rich-content rich-content-blocks" data-testid={testId}>
      {blocks.map((b, i) => {
        if (b.type === 'table') {
          return (
            <TableEditor
              key={b.id}
              parsed={b.parsed}
              lang={lang}
              testId={`${testId}-table-${i}`}
              onChange={(next) => updateBlock(i, { ...b, parsed: next, raw: serializeTableBlock(next) })}
            />
          );
        }
        return (
          <TextBlockEditor
            key={b.id}
            block={b}
            lang={lang}
            testId={`${testId}-block-${i}`}
            onChange={(next) => updateBlock(i, next)}
            onDelete={() => deleteBlock(i)}
          />
        );
      })}
      {adding && (
        <div className="mt-2">
          <textarea
            ref={addRef}
            defaultValue=""
            rows={3}
            placeholder={lang === 'de' ? 'Neuen Textblock schreiben…' : 'Write a new text block…'}
            onBlur={(e) => {
              const v = e.target.value;
              setAdding(false);
              appendFromText(v);
            }}
            className="w-full p-3 rounded-lg border-2 border-[#1e9ead] focus:outline-none font-mono text-[13px] leading-relaxed bg-gray-50/70 text-[#0a1628] resize-y no-print"
            data-testid={`${testId}-add-textarea`}
          />
        </div>
      )}
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="mt-2 text-xs font-medium text-[#1e9ead] hover:text-[#0d7278] transition-colors no-print opacity-0 group-hover:opacity-100 focus:opacity-100"
        data-testid={`${testId}-add-block`}
        style={{ opacity: adding ? 0 : undefined }}
      >{L.addBlock}</button>
    </div>
  );
};

function syntaxHighlight(html) {
  return escapeHtml(html)
    .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span style="color:#6b7280">$1</span>')
    .replace(/(&lt;\/?[a-z][a-z0-9-]*\b[^&]*?&gt;)/g, '<span style="color:#1e9ead">$1</span>');
}

// ─────────────────────────────────────────────────────────────
// HtmlEditor — editable, syntax-highlighted code editor for the HTML tab.
// - Textarea holds the editable source; a mirrored <pre> below shows the
//   currently-parsed live HTML preview for reassurance (updates on Apply).
// - "Übernehmen" button validates + applies the HTML back to blog_structured.
// - Before each Apply, we snapshot the CURRENT blog_structured onto an undo
//   stack (last 5 states). "Rückgängig" pops the stack and restores.
// - Dirty banner appears when local edits diverge from the preview state so
//   the user can either apply or reset without confusion.
// ─────────────────────────────────────────────────────────────
const UNDO_STACK_LIMIT = 5;

const HtmlEditor = ({ cleanHtml, currentBlog, onApply, onRestore, onCopy, copied, lang, undoStack, setUndoStack }) => {
  const [draft, setDraft] = useState(cleanHtml);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);
  const [restoredFlash, setRestoredFlash] = useState(false);
  const lastSyncedRef = useRef(cleanHtml);

  // Resync draft when the preview state changed externally (e.g. block edits
  // in the Preview tab, or a save-triggered reload). Only overwrite the draft
  // if the user hasn't diverged from what we last synced.
  useEffect(() => {
    if (cleanHtml !== lastSyncedRef.current && draft === lastSyncedRef.current) {
      setDraft(cleanHtml);
      lastSyncedRef.current = cleanHtml;
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanHtml]);

  const dirty = draft !== lastSyncedRef.current;

  const L = lang === 'de'
    ? { apply: 'Übernehmen', reset: 'Zurücksetzen', applied: 'Übernommen', dirty: 'Ungespeicherte HTML-Änderungen', title: 'HTML-Editor', hint: 'Änderungen werden nach „Übernehmen" in die Vorschau übertragen.', undo: 'Rückgängig', undoTitle: 'Letzte Übernahme rückgängig machen', restored: 'Wiederhergestellt' }
    : { apply: 'Apply',      reset: 'Reset',        applied: 'Applied',    dirty: 'Unsaved HTML changes',           title: 'HTML editor',   hint: 'Click "Apply" to sync your changes back to the preview.', undo: 'Undo',      undoTitle: 'Undo last apply',                  restored: 'Restored' };

  const doApply = () => {
    setError(null);
    // Snapshot the CURRENT blog state BEFORE we mutate it, so Undo can restore.
    // Deep-copy so later edits to blogData don't mutate the snapshot.
    let snapshot = null;
    try {
      snapshot = currentBlog ? JSON.parse(JSON.stringify(currentBlog)) : null;
    } catch { snapshot = null; }

    const res = onApply(draft);
    if (res && res.ok) {
      if (snapshot) {
        setUndoStack(prev => [...prev, snapshot].slice(-UNDO_STACK_LIMIT));
      }
      lastSyncedRef.current = draft;
      setApplied(true);
      setTimeout(() => setApplied(false), 1500);
    } else {
      setError(res?.error || (lang === 'de' ? 'Unbekannter Fehler.' : 'Unknown error.'));
    }
  };

  const doUndo = () => {
    if (!undoStack.length || !onRestore) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack(undoStack.slice(0, -1));
    onRestore(prev);
    setError(null);
    setRestoredFlash(true);
    setTimeout(() => setRestoredFlash(false), 1500);
  };

  const doReset = () => {
    setDraft(lastSyncedRef.current);
    setError(null);
  };

  return (
    <div className="relative" data-testid="blog-viewer-html-editor">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-wider font-semibold text-[#1e9ead]">
            {L.title}
          </span>
          {dirty && !applied && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800" data-testid="blog-html-dirty-badge">
              ● {L.dirty}
            </span>
          )}
          {applied && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800" data-testid="blog-html-applied-badge">
              ✓ {L.applied}
            </span>
          )}
          {restoredFlash && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800" data-testid="blog-html-restored-badge">
              ↺ {L.restored}
            </span>
          )}
          {undoStack.length > 0 && (
            <span className="text-[10px] text-slate-500" data-testid="blog-html-undo-count">
              {undoStack.length}/{UNDO_STACK_LIMIT}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {undoStack.length > 0 && (
            <button type="button" onClick={doUndo}
              title={L.undoTitle}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              data-testid="blog-html-undo">
              ↺ {L.undo}
            </button>
          )}
          {dirty && (
            <button type="button" onClick={doReset}
              className="text-xs font-medium text-slate-600 hover:text-slate-800"
              data-testid="blog-html-reset">
              {L.reset}
            </button>
          )}
          <button type="button" onClick={doApply}
            disabled={!dirty}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${dirty ? 'bg-[#1e9ead] text-white hover:bg-[#197b87]' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
            data-testid="blog-html-apply">
            {L.apply}
          </button>
          <button type="button" onClick={onCopy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-white hover:opacity-90 transition-opacity"
            style={{ background: '#1e9ead' }}
            data-testid="blog-viewer-copy-html">
            {copied ? <><Check className="w-3.5 h-3.5" /> {lang === 'de' ? 'Kopiert' : 'Copied'}</> : <><Copy className="w-3.5 h-3.5" /> {lang === 'de' ? 'Kopieren' : 'Copy'}</>}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mb-2">{L.hint}</p>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        className="w-full rounded-lg bg-[#0a1628] text-gray-200 text-xs leading-relaxed p-5 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1e9ead]"
        style={{ minHeight: '55vh' }}
        data-testid="blog-html-textarea"
      />
      {error && (
        <div className="mt-3 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert" data-testid="blog-html-error">
          <strong className="block font-semibold mb-1">{lang === 'de' ? 'HTML konnte nicht übernommen werden' : 'HTML could not be applied'}</strong>
          <span className="text-xs">{error}</span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// HTML → blog_structured parser (reverse of buildCleanHtml).
// Parses a full <article> DOM into the block-based structure so edits made in
// the HTML view flow back to the block/preview view.
// Returns { ok: true, blog } on success, { ok: false, error: '...' } on failure.
// ─────────────────────────────────────────────────────────────
function htmlBlockToRaw(node) {
  // Convert one DOM element back into the markdown block syntax the block
  // architecture understands. Called for each element that lives inside a
  // <section>'s body.
  const tag = (node.tagName || '').toLowerCase();
  const text = (node.innerText || node.textContent || '').trim();
  if (tag === 'p') return text;
  if (tag === 'ul') {
    return Array.from(node.querySelectorAll(':scope > li'))
      .map(li => `- ${(li.innerText || li.textContent || '').trim()}`)
      .join('\n');
  }
  if (tag === 'ol') {
    const items = Array.from(node.querySelectorAll(':scope > li'));
    return items.map((li, i) => {
      const stepText = li.querySelector('.step-text');
      const val = stepText ? (stepText.innerText || stepText.textContent || '').trim()
                           : (li.innerText || li.textContent || '').trim();
      return `${i + 1}. ${val}`;
    }).join('\n');
  }
  if (tag === 'div' && node.classList.contains('table-wrap')) {
    const table = node.querySelector('table');
    if (!table) return '';
    const headers = Array.from(table.querySelectorAll('thead th')).map(th => (th.innerText || '').trim());
    const rows = Array.from(table.querySelectorAll('tbody tr')).map(tr =>
      Array.from(tr.querySelectorAll('td')).map(td => (td.innerText || '').trim())
    );
    if (!headers.length) return '';
    const lines = [
      `| ${headers.join(' | ')} |`,
      `| ${headers.map(() => '---').join(' | ')} |`,
      ...rows.map(r => `| ${r.join(' | ')} |`),
    ];
    return lines.join('\n');
  }
  if (tag === 'table') {
    // bare table without wrapper — same logic
    return htmlBlockToRaw({ tagName: 'DIV', classList: { contains: (c) => c === 'table-wrap' }, querySelector: () => node });
  }
  if (tag === 'aside' && node.classList.contains('callout')) {
    let type = 'INFO';
    if (node.classList.contains('callout-einordnung')) type = 'EINORDNUNG';
    else if (node.classList.contains('callout-summary')) type = 'ZUSAMMENFASSUNG';
    else if (node.classList.contains('callout-important')) type = 'WICHTIG';
    const body = node.querySelector('.callout-text');
    const bodyText = body ? (body.innerText || body.textContent || '').trim()
                          : (node.innerText || node.textContent || '').replace(/^\s*(Info|Einordnung|Zusammenfassung|Wichtig|Fazit)\s*/i, '').trim();
    return `> ${type}: ${bodyText}`;
  }
  // Unknown tag → fall back to plain text
  return text;
}

function htmlSectionToBody(sectionEl) {
  // Concatenate every direct child (except the leading h2) into markdown blocks.
  const blocks = [];
  Array.from(sectionEl.children).forEach(child => {
    if (child.tagName === 'H2') return;
    if (child.tagName === 'H3') {
      // h3_blocks: not part of the section body; caller handles separately.
      return;
    }
    const raw = htmlBlockToRaw(child);
    if (raw && raw.trim()) blocks.push(raw);
  });
  return blocks.join('\n\n');
}

function htmlToBlogStructured(html, prevBlog) {
  try {
    const parser = new DOMParser();
    // Wrap fragment so DOMParser accepts it whether the input is a full doc,
    // an <article> fragment, or just a bare block list.
    const wrapped = `<!DOCTYPE html><html><body>${html}</body></html>`;
    const doc = parser.parseFromString(wrapped, 'text/html');
    const article = doc.querySelector('article') || doc.body;
    if (!article) return { ok: false, error: 'Kein <article> oder <body>-Element gefunden.' };

    const meta = { ...(prevBlog?.meta || {}) };
    const h1 = article.querySelector('h1');
    if (h1) meta.title = (h1.innerText || h1.textContent || '').trim();

    // Meta comments <!-- Meta Title: ... --> etc.
    const commentIterator = doc.evaluate('//comment()', doc, null, 0, null);
    let cmt;
    while ((cmt = commentIterator.iterateNext())) {
      const c = cmt.nodeValue || '';
      const m = c.match(/^\s*Meta Title:\s*(.+?)\s*$/i);
      if (m) meta.meta_title = m[1];
      const md = c.match(/^\s*Meta Description:\s*(.+?)\s*$/i);
      if (md) meta.meta_description = md[1];
      const ms = c.match(/^\s*Slug:\s*\/?blog\/?(.+?)\s*$/i);
      if (ms) meta.slug = ms[1];
      const mk = c.match(/^\s*Main Keyword:\s*(.+?)\s*$/i);
      if (mk) meta.main_keyword = mk[1];
    }

    const intro = (article.querySelector('p.intro')?.innerText || '').trim();
    const conclusion = (article.querySelector('section#conclusion')?.innerText || '').replace(/^Fazit\s*/i, '').replace(/^Conclusion\s*/i, '').trim();

    const takeawayLis = article.querySelectorAll('.key-takeaways ul li');
    const key_takeaways = Array.from(takeawayLis).map(li => (li.innerText || li.textContent || '').trim()).filter(Boolean);

    // Sections: every <section id="section-*">
    const sectionEls = article.querySelectorAll('section[id^="section-"]');
    const sections = Array.from(sectionEls).map(sEl => {
      const h2El = sEl.querySelector('h2');
      const h2 = h2El ? (h2El.innerText || '').trim() : '';
      const content = htmlSectionToBody(sEl);
      const h3_blocks = [];
      let currentH3 = null;
      Array.from(sEl.children).forEach(child => {
        if (child.tagName === 'H3') {
          currentH3 = { h3: (child.innerText || '').trim(), content: '' };
          h3_blocks.push(currentH3);
        } else if (currentH3 && child.tagName !== 'H2') {
          const raw = htmlBlockToRaw(child);
          if (raw && raw.trim()) currentH3.content += (currentH3.content ? '\n\n' : '') + raw;
        }
      });
      return { h2, content, h3_blocks };
    });

    // FAQ
    const faqEls = article.querySelectorAll('#faq .faq-item');
    const faq = Array.from(faqEls).map(el => ({
      question: (el.querySelector('h3')?.innerText || '').trim(),
      answer:   (el.querySelector('p')?.innerText || '').trim(),
    })).filter(x => x.question || x.answer);

    return {
      ok: true,
      blog: {
        ...(prevBlog || {}),
        meta,
        intro,
        key_takeaways,
        sections,
        conclusion,
        faq,
        // sources & image_* fields preserved from prevBlog (not editable via HTML view)
      },
    };
  } catch (e) {
    return { ok: false, error: `HTML konnte nicht verarbeitet werden: ${e?.message || e}` };
  }
}

// Build a self-contained printable HTML document for clean PDF export.
// Whitepaper-style: serif body font, generous margins, no app chrome.
function buildPrintableDocument(blogData, t, opts = {}) {
  const meta = blogData?.meta || {};
  const sections = (blogData?.sections || []).filter(s => s && (s.content || s.body || s.h2 || s.header || s.headline));
  const faq = blogData?.faq || [];
  const takeaways = blogData?.key_takeaways || [];
  const title = meta.title || opts.fallbackTitle || blogData?.title || blogData?.headline || blogData?.h1 || '';
  const intro = blogData?.intro || '';
  const conclusion = blogData?.conclusion || '';
  const author = blogData?.author || 'xchannels.ai';
  const publishedDate = blogData?.published_date || new Date().toLocaleDateString(opts.lang === 'de' ? 'de-DE' : 'en-US');

  const tocItems = sections.map((s, i) => `<li><a href="#section-${i}">${escapeHtml(s.h2 || s.header || s.headline || `${t.toc} ${i + 1}`)}</a></li>`).join('');
  const sectionsHtml = sections.map((s, i) => {
    const h = s.h2 || s.header || s.headline || '';
    const body = s.content || s.body || '';
    return `<section id="section-${i}"><h2>${escapeHtml(h)}</h2>${body ? paragraphsToHtml(body) : ''}</section>`;
  }).join('\n');
  const faqHtml = faq.length ? `<section class="faq-section"><h2>${escapeHtml(t.faq)}</h2>${faq.map(f => `<div class="faq-item"><h3>${escapeHtml(f?.question || '')}</h3><p>${escapeHtml(f?.answer || '')}</p></div>`).join('')}</section>` : '';
  const takeawaysHtml = takeaways.length ? `<aside class="key-takeaways"><strong>${escapeHtml(t.takeaways)}</strong><ul>${takeaways.map(k => `<li>${escapeHtml(k)}</li>`).join('')}</ul></aside>` : '';

  const css = `
    @page { size: A4; margin: 2cm 2cm 2cm 2cm; }
    * { box-sizing: border-box; }
    html, body { background: #ffffff; }
    body {
      font-family: Georgia, 'Iowan Old Style', 'Times New Roman', serif;
      color: #0a1628;
      line-height: 1.7;
      font-size: 11pt;
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    article { max-width: 720px; margin: 0 auto; padding: 24px 0; }
    h1 { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 26pt; line-height: 1.2; margin: 0 0 12px; color: #0a1628; }
    h2 { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 16pt; margin: 28px 0 12px; color: #0a1628; page-break-after: avoid; }
    h3 { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 13pt; margin: 18px 0 8px; color: #0a1628; }
    p { margin: 0 0 12px; }
    a { color: #1e9ead; text-decoration: none; }
    ul, ol { padding-left: 22px; }
    .article-meta { font-size: 10pt; color: #6b7280; margin: 0 0 28px; border-bottom: 1px solid #e5e7eb; padding-bottom: 16px; }
    .intro { font-size: 12pt; font-weight: 500; margin-bottom: 24px; }
    .table-of-contents { background: #f0fdfc; border-left: 3px solid #1e9ead; padding: 14px 18px; margin: 24px 0; page-break-inside: avoid; }
    .table-of-contents strong { display: block; margin-bottom: 8px; font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; text-transform: uppercase; letter-spacing: 0.5px; }
    .table-of-contents ul { margin: 0; padding-left: 18px; }
    .table-of-contents li { margin: 4px 0; }
    .key-takeaways { background: #f0fdfc; border-left: 3px solid #1e9ead; padding: 14px 18px; margin: 0 0 28px; page-break-inside: avoid; }
    .key-takeaways strong { display: block; margin-bottom: 8px; font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; text-transform: uppercase; letter-spacing: 0.5px; }
    .key-takeaways ul { margin: 0; padding-left: 18px; }
    section { page-break-inside: auto; margin-bottom: 8px; }
    .faq-section { margin-top: 32px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
    .faq-item { margin-bottom: 16px; page-break-inside: avoid; }
    .faq-item h3 { font-size: 12pt; color: #1e9ead; margin: 0 0 4px; }
    .faq-item p { margin: 0; }

    ul.rich-bullets { list-style: none; padding-left: 0; margin: 12pt 0; }
    ul.rich-bullets li { position: relative; padding-left: 18pt; margin: 5pt 0; line-height: 1.55; list-style: none; }
    ul.rich-bullets li::before { content: ""; position: absolute; left: 4pt; top: 6.5pt; width: 4pt; height: 4pt; border-radius: 999px; background: #1e9ead; }

    ol.rich-steps { list-style: none; padding-left: 0; margin: 12pt 0; }
    ol.rich-steps li { display: flex; align-items: flex-start; gap: 8pt; margin: 6pt 0; padding: 6pt 8pt; background: #fafbfc; border: 1px solid #eef0f3; border-radius: 6px; line-height: 1.5; page-break-inside: avoid; list-style: none; }
    ol.rich-steps li .step-num { flex: 0 0 20pt; height: 20pt; border-radius: 999px; background: #1e9ead; color: #fff; font-weight: 700; font-size: 9pt; display: inline-flex; align-items: center; justify-content: center; }
    ol.rich-steps li .step-text { flex: 1; padding-top: 1pt; }

    .table-wrap { margin: 12pt 0; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden; }
    table.rich-table { width: 100%; border-collapse: collapse; font-size: 10pt; }
    table.rich-table thead { background: #f0fdfc; }
    table.rich-table th { text-align: left; font-weight: 700; color: #0a1628; padding: 7pt 9pt; border-bottom: 2px solid #1e9ead; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.4pt; }
    table.rich-table td { padding: 7pt 9pt; border-bottom: 1px solid #f1f5f9; vertical-align: top; line-height: 1.45; }
    table.rich-table tbody tr:nth-child(even) td { background: #fafbfc; }
    table.rich-table tbody tr:last-child td { border-bottom: 0; }

    .callout { display: flex; gap: 7pt; align-items: flex-start; margin: 12pt 0; padding: 8pt 10pt; border-radius: 6px; page-break-inside: avoid; }
    .callout-icon { flex: 0 0 auto; }
    .callout-body { flex: 1; }
    .callout-label { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4pt; margin-bottom: 2pt; }
    .callout-text { font-size: 10pt; line-height: 1.5; }
    .callout-info { background: #f0f9ff; border: 1px solid #bae6fd; border-left: 3px solid #0284c7; }
    .callout-info .callout-icon, .callout-info .callout-label { color: #0284c7; }
    .callout-einordnung { background: #f0fdfa; border: 1px solid #99f6e4; border-left: 3px solid #0d9488; }
    .callout-einordnung .callout-icon, .callout-einordnung .callout-label { color: #0d9488; }
    .callout-summary { background: #fffbeb; border: 1px solid #fde68a; border-left: 4px solid #d97706; }
    .callout-summary .callout-icon, .callout-summary .callout-label { color: #b45309; }
    .callout-summary .callout-text { font-weight: 500; }
    .callout-important { background: #fff1f2; border: 1px solid #fecdd3; border-left: 4px solid #e11d48; }
    .callout-important .callout-icon, .callout-important .callout-label { color: #be123c; }
    .callout-important .callout-text { font-weight: 500; }

    @media screen {
      body { padding: 24px; background: #f5f5f5; }
      article { background: #fff; padding: 48px; border-radius: 4px; box-shadow: 0 2px 12px rgba(0,0,0,0.08); }
    }
  `;

  return `<!DOCTYPE html>
<html lang="${opts.lang || 'de'}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(meta.meta_title || title)}</title>
${meta.meta_description ? `<meta name="description" content="${escapeHtml(meta.meta_description)}">` : ''}
<style>${css}</style>
</head>
<body>
<article>
  <h1>${escapeHtml(title)}</h1>
  <p class="article-meta">${escapeHtml(t.by)} ${escapeHtml(author)} · ${escapeHtml(publishedDate)}${meta.reading_time_minutes ? ` · ${meta.reading_time_minutes} ${escapeHtml(t.reading_time)}` : ''}</p>
  ${intro ? `<p class="intro">${escapeHtml(intro)}</p>` : ''}
  ${sections.length ? `<nav class="table-of-contents"><strong>${escapeHtml(t.toc)}</strong><ul>${tocItems}</ul></nav>` : ''}
  ${takeawaysHtml}
  ${sectionsHtml}
  ${conclusion ? `<section id="conclusion"><h2>${escapeHtml(t.conclusion)}</h2>${paragraphsToHtml(conclusion)}</section>` : ''}
  ${faqHtml}
</article>
</body>
</html>`;
}

// ─────────────────────────── Component ───────────────────────────
const BlogArticleViewer = forwardRef(function BlogArticleViewer(
  { blogData: initialBlogData, fallbackTitle = '', heroImageUrl = '', primaryColor = '#1e9ead', language, onSave, onSaveSuccess, articleKey },
  ref
) {
  const [view, setView] = useState('preview');
  const [copied, setCopied] = useState(false);
  // Undo stack for HTML-Apply operations. Lives in the parent so it survives
  // tab-switches (HtmlEditor unmounts on Preview→HTML tab change).
  const [htmlUndoStack, setHtmlUndoStack] = useState([]);
  const [metaOpen, setMetaOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState({});
  const [blogData, setBlogData] = useState(initialBlogData);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Only sync blogData from prop when the underlying content piece changes
  // (NOT on every parent re-render, which would wipe inline edits).
  // articleKey (when provided by parent) takes precedence — bumping it forces
  // a re-sync (e.g. after a save in a sibling viewer instance).
  const initialFingerprint = useMemo(() => {
    if (articleKey != null) return String(articleKey);
    if (!initialBlogData) return null;
    const m = initialBlogData.meta || {};
    return [
      m.title || '',
      m.slug || '',
      (initialBlogData.sections || []).length,
      (initialBlogData.intro || '').slice(0, 40),
    ].join('|');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBlogData, articleKey]);

  const lastFingerprint = useRef(initialFingerprint);
  // Snapshot of what is saved, so the toolbar can show "Änderungen speichern" only when needed.
  const savedSnapshot = useRef(JSON.stringify(initialBlogData ?? null));
  useEffect(() => {
    if (initialFingerprint !== lastFingerprint.current) {
      lastFingerprint.current = initialFingerprint;
      savedSnapshot.current = JSON.stringify(initialBlogData ?? null);
      setBlogData(initialBlogData);
    }
  }, [initialFingerprint, initialBlogData]);

  // Per-instance section refs — avoids document.getElementById collisions
  // when multiple BlogArticleViewer instances render `id="section-0"` etc.
  // (e.g. sidebar + expanded modal both mounted at once).
  const sectionRefs = useRef([]);
  const scrollToSection = (idx) => {
    const el = sectionRefs.current[idx];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const lang = useMemo(() => detectLanguage(blogData, language), [blogData, language]);
  const t = LABELS[lang] || LABELS.en;
  const accent = primaryColor || '#1e9ead';

  const meta = blogData?.meta || {};
  const title = resolveTitle(blogData, fallbackTitle);
  const intro = blogData?.intro || '';
  const sections = (blogData?.sections || []);
  const takeaways = blogData?.key_takeaways || [];
  const conclusion = blogData?.conclusion || '';
  const faq = blogData?.faq || [];

  // Mutation helpers
  const updateField = (path, value) => {
    setBlogData(prev => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const segs = path.split('.');
      let cur = next;
      for (let i = 0; i < segs.length - 1; i++) {
        const k = segs[i];
        if (cur[k] === undefined || cur[k] === null) cur[k] = isNaN(Number(segs[i + 1])) ? {} : [];
        cur = cur[k];
      }
      cur[segs[segs.length - 1]] = value;
      return next;
    });
  };

  // Total word count
  const totalWords = useMemo(() => {
    let n = countWords(intro) + countWords(conclusion);
    sections.forEach(s => { n += countWords(secBody(s)); (s.h3_blocks || []).forEach(h => n += countWords(h.content || h.body || '')); });
    return n;
  }, [intro, conclusion, sections]);

  const cleanHtml = useMemo(() => buildCleanHtml(blogData, t, { fallbackTitle, lang, heroImageUrl }), [blogData, t, fallbackTitle, lang, heroImageUrl]);
  const standaloneHtml = useMemo(() => buildCleanHtml(blogData, t, { fallbackTitle, lang, standalone: true }), [blogData, t, fallbackTitle, lang]);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(cleanHtml); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
  };

  // PDF export — builds a self-contained standalone HTML document and opens
  // it in a new tab via a Blob URL (more reliable than document.write, which
  // iPadOS Safari often silently drops, leaving an empty about:blank tab).
  // The user then prints / saves as PDF from there.
  const handleExportPdf = () => {
    const docHtml = buildPrintableDocument(blogData, t, { lang, fallbackTitle });
    const blob = new Blob([docHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    if (!w) {
      // Popup blocked — offer download as a fallback so user still gets the file
      const a = document.createElement('a');
      a.href = url;
      a.download = `${meta.slug || 'blog-artikel'}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Desktop browsers: try auto-print after the document loads. iOS Safari
      // ignores this — user prints via the iOS share menu, which is fine.
      const tryPrint = () => { try { w.focus(); w.print(); } catch (_) {} };
      try { w.addEventListener('load', () => setTimeout(tryPrint, 200)); } catch (_) {}
      setTimeout(tryPrint, 1500);
    }
    // Free the Blob URL after the new tab has had time to load it
    setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
  };

  const handleExportHtml = () => {
    const slug = meta.slug || 'blog-artikel';
    const blob = new Blob([standaloneHtml], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${slug}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      // Pass the rich fragment so the parent can persist it (content_html) —
      // keeps buildCleanHtml the single renderer for server-side republish.
      await onSave(blogData, cleanHtml);
      savedSnapshot.current = JSON.stringify(blogData ?? null);
      setSavedAt(Date.now());
      if (typeof onSaveSuccess === 'function') {
        onSaveSuccess(blogData);
      }
    }
    catch (_) { /* parent handles toast */ }
    finally { setSaving(false); }
  };

  // Expose save() so parent components can trigger it from external buttons
  // (e.g. the modal footer "Speichern" button in Produktion.js).
  useImperativeHandle(ref, () => ({
    save: handleSave,
    getBlogData: () => blogData,
    getCleanHtml: () => cleanHtml,
  }), [blogData, onSave, onSaveSuccess, cleanHtml]);

  const hasMeta = Boolean(meta.meta_title || meta.meta_description || meta.slug || meta.main_keyword
    || blogData?.image_filename_suggestion || blogData?.image_alt_suggestion);

  const metaTitleLen = (meta.meta_title || '').length;
  const metaTitleOk = metaTitleLen >= 50 && metaTitleLen <= 60;
  const metaDescLen = (meta.meta_description || '').length;
  const metaDescOk = metaDescLen >= 140 && metaDescLen <= 155;

  return (
    <div className="blog-article-viewer w-full" style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}>
      <style>{`
        .blog-article-viewer [contenteditable]:empty::before { content: attr(data-placeholder); color: #94A3B8; pointer-events: none; }

        /* ── Rich content (live preview / WYSIWYG view) ────────────────── */
        .rich-content { color: #0a1628; line-height: 1.75; font-size: 1rem; }
        .rich-content > * + * { margin-top: 1.25rem; }
        .rich-content p { margin: 0; font-size: 1rem; line-height: 1.75; }
        .rich-content p + p { margin-top: 1.25rem; }
        .intro-block .rich-content p { font-size: 1.125rem; font-weight: 500; line-height: 1.7; }
        .rich-content .rich-empty { color: #94A3B8; font-style: italic; }

        /* Bullet lists */
        .rich-content ul.rich-bullets { list-style: none; padding-left: 0; margin: 1.25rem 0; }
        .rich-content ul.rich-bullets li {
          position: relative; padding-left: 1.75rem; margin: 0.6rem 0;
          line-height: 1.65; list-style: none;
        }
        .rich-content ul.rich-bullets li::before {
          content: ""; position: absolute; left: 0.5rem; top: 0.7rem;
          width: 6px; height: 6px; border-radius: 999px; background: #1e9ead;
        }

        /* Numbered lists / Step cards */
        .rich-content ol.rich-steps { list-style: none; padding-left: 0; margin: 1.5rem 0; counter-reset: step; }
        .rich-content ol.rich-steps li {
          display: flex; align-items: flex-start; gap: 0.875rem;
          margin: 0.875rem 0; padding: 0.75rem 1rem 0.75rem 0.75rem;
          background: #fafbfc; border: 1px solid #eef0f3; border-radius: 8px;
          line-height: 1.6; list-style: none;
        }
        .rich-content ol.rich-steps li .step-num {
          flex: 0 0 28px; height: 28px; border-radius: 999px;
          background: #1e9ead; color: #fff;
          font-weight: 700; font-size: 0.85rem;
          display: inline-flex; align-items: center; justify-content: center;
          line-height: 1;
        }
        .rich-content ol.rich-steps li .step-text { flex: 1 1 auto; padding-top: 0.18rem; }

        /* Callouts */
        .rich-content .callout {
          display: flex; gap: 0.85rem; align-items: flex-start;
          margin: 1.5rem 0; padding: 1rem 1.15rem;
          border-radius: 10px;
          break-inside: avoid; page-break-inside: avoid;
        }
        .rich-content .callout-icon { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; padding-top: 0.1rem; }
        .rich-content .callout-body { flex: 1 1 auto; min-width: 0; }
        .rich-content .callout-label {
          font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
          font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.06em; margin-bottom: 0.2rem;
        }
        .rich-content .callout-text { font-size: 0.95rem; line-height: 1.65; }
        .rich-content .callout-info {
          background: #f0f9ff; border: 1px solid #bae6fd; border-left: 3px solid #0284c7;
        }
        .rich-content .callout-info .callout-icon, .rich-content .callout-info .callout-label { color: #0284c7; }
        .rich-content .callout-einordnung {
          background: #f0fdfa; border: 1px solid #99f6e4; border-left: 3px solid #0d9488;
        }
        .rich-content .callout-einordnung .callout-icon, .rich-content .callout-einordnung .callout-label { color: #0d9488; }
        .rich-content .callout-summary {
          background: #fffbeb; border: 1px solid #fde68a; border-left: 4px solid #d97706;
          box-shadow: 0 1px 2px rgba(217,119,6,0.08);
        }
        .rich-content .callout-summary .callout-icon, .rich-content .callout-summary .callout-label { color: #b45309; }
        .rich-content .callout-summary .callout-text { font-weight: 500; }
        .rich-content .callout-important {
          background: #fff1f2; border: 1px solid #fecdd3; border-left: 4px solid #e11d48;
        }
        .rich-content .callout-important .callout-icon, .rich-content .callout-important .callout-label { color: #be123c; }
        .rich-content .callout-important .callout-text { font-weight: 500; }

        /* Tables */
        .rich-content .table-wrap { overflow-x: auto; margin: 1.5rem 0; border-radius: 10px; border: 1px solid #e5e7eb; }
        .rich-content table.rich-table {
          width: 100%; min-width: 480px; border-collapse: collapse;
          font-size: 0.93rem; background: #fff;
        }
        .rich-content table.rich-table thead { background: #f0fdfc; }
        .rich-content table.rich-table th {
          text-align: left; font-weight: 700; color: #0a1628;
          padding: 0.85rem 1rem; border-bottom: 2px solid #1e9ead;
          font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.04em;
        }
        .rich-content table.rich-table td {
          padding: 0.85rem 1rem; border-bottom: 1px solid #f1f5f9;
          vertical-align: top; line-height: 1.55;
        }
        .rich-content table.rich-table tbody tr:nth-child(even) td { background: #fafbfc; }
        .rich-content table.rich-table tbody tr:hover td { background: #ecfeff; }
        .rich-content table.rich-table tbody tr:last-child td { border-bottom: 0; }

        /* H3 within rich content */
        .rich-content h3 { font-size: 1.125rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #0a1628; }

        /* Native in-place list items — JSX-rendered <li> using flex layout */
        .rich-content li.rich-bullet-li {
          list-style: none; position: relative;
          padding-left: 1.75rem; margin: 0.6rem 0; line-height: 1.65;
          display: flex; align-items: baseline;
        }
        .rich-content li.rich-bullet-li::before {
          content: ""; position: absolute; left: 0.5rem; top: 0.7rem;
          width: 6px; height: 6px; border-radius: 999px; background: #1e9ead;
        }
        .rich-content li.rich-bullet-add,
        .rich-content li.rich-step-add {
          list-style: none; padding: 0; margin: 0.5rem 0 0;
          background: transparent !important; border: 0 !important;
          display: block;
        }
        .rich-content ol.rich-steps li:not(.rich-step-add) {
          list-style: none;
        }
        .rich-content .callout-label.print-only { display: none; }
        @media print {
          .rich-content .callout-label.print-only { display: block; }
        }
        .rich-content [contenteditable]:empty::before {
          content: attr(data-placeholder); color: #cbd5e1; font-style: italic; pointer-events: none;
        }

        /* Print/PDF — same rules apply */
        @media print {
          .rich-content .callout, .rich-content .table-wrap { break-inside: avoid; page-break-inside: avoid; }
          .rich-content table.rich-table tbody tr:hover td { background: transparent; }
          .table-editor .te-toolbar, .table-editor .te-cell-del, .table-editor .te-row-del { display: none !important; }
        }

        /* TableEditor — per-cell editing */
        .table-editor { margin: 1.5rem 0; }
        .table-editor .te-toolbar { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; }
        .table-editor .te-btn {
          font-size: 0.72rem; padding: 0.3rem 0.7rem; border-radius: 999px;
          background: #f0fdfc; color: #115e59; border: 1px solid #99f6e4;
          font-weight: 600; cursor: pointer; transition: background 0.15s;
        }
        .table-editor .te-btn:hover { background: #ccfbf1; }
        .table-editor .te-table th, .table-editor .te-table td { position: relative; }
        .table-editor .te-th-inner, .table-editor .te-td-inner { display: flex; align-items: flex-start; gap: 0.25rem; }
        .table-editor .te-cell {
          flex: 1 1 auto; outline: none; min-height: 1.2em;
          padding: 0.1rem 0.2rem; border-radius: 4px;
          transition: background 0.12s, box-shadow 0.12s;
          cursor: text;
        }
        .table-editor .te-cell:hover { background: #f0fdfc; }
        .table-editor .te-cell:focus { background: #ecfeff; box-shadow: 0 0 0 2px #5eead4 inset; }
        .table-editor .te-cell:empty::before { content: attr(data-placeholder); color: #cbd5e1; font-style: italic; }
        .table-editor .te-th-cell { font-weight: 700; }
        .table-editor .te-cell-del, .table-editor .te-row-del {
          flex: 0 0 auto; width: 18px; height: 18px; line-height: 14px;
          border-radius: 999px; background: transparent; color: #94a3b8;
          border: none; font-size: 16px; cursor: pointer;
          opacity: 0; transition: opacity 0.15s, color 0.15s;
        }
        .table-editor tr:hover .te-cell-del,
        .table-editor tr:hover .te-row-del,
        .table-editor th:hover .te-cell-del { opacity: 1; }
        .table-editor .te-cell-del:hover, .table-editor .te-row-del:hover { color: #e11d48; }
      `}</style>

      {/* ── Toolbar: same bar as the newsletter viewer ── */}
      <div className="no-print mb-6">
        <ViewerToolbar
          testIdPrefix="blog-viewer"
          tabs={[{ key: 'preview', label: t.preview }, { key: 'html', label: t.html_view }]}
          active={view}
          onTab={setView}
          dirty={JSON.stringify(blogData ?? null) !== savedSnapshot.current}
          saving={saving}
          onSave={onSave ? handleSave : undefined}
          saveLabel={t.save}
          exportLabel={lang === 'de' ? 'Exportieren' : 'Export'}
          exportItems={[
            { label: t.export_pdf, icon: <FileDown size={14} />, onClick: handleExportPdf, testId: 'blog-viewer-export-pdf' },
            { label: t.export_html, icon: <FileCode2 size={14} />, onClick: handleExportHtml, testId: 'blog-viewer-export-html' },
            { label: lang === 'de' ? 'HTML kopieren' : 'Copy HTML', icon: <Copy size={14} />, onClick: async () => { await handleCopy(); }, doneLabel: t.copied, testId: 'blog-viewer-copy-html' },
          ]}
        />
      </div>

      {/* ── PREVIEW (light theme, only this prints) ── */}
      {view === 'preview' && (
        <div id="blog-print-area" className="preview-container max-w-[720px] mx-auto bg-white p-6 md:p-8 rounded-xl"
             style={{ color: '#0a1628' }} data-testid="blog-viewer-preview">

          {/* Total counter */}
          <div className="no-print text-xs text-gray-400 mb-3" data-testid="blog-total-words">{t.total}: {totalWords} {t.words}</div>

          {/* H1 — editable */}
          <Editable as="h1" value={title} onChange={(v) => updateField('meta.title', v)}
            className="text-3xl md:text-4xl font-bold leading-tight mb-3"
            style={{ color: '#0a1628' }}
            testId="blog-edit-title" />

          {/* Search-intent badge (no-print) — verifies pipeline classification */}
          {(() => {
            const intentKey = meta?.search_intent;
            if (!intentKey) return null;
            const labels = INTENT_LABELS[lang] || INTENT_LABELS.en;
            const info = labels[intentKey] || labels.informational;
            const cls = INTENT_BADGE_CLASSES[info.color] || INTENT_BADGE_CLASSES.slate;
            return (
              <div className="no-print mb-4" data-testid="blog-intent-badge-row">
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${cls}`}
                  data-testid={`blog-intent-badge-${intentKey}`}
                  title={lang === 'de' ? 'Erkannte Suchintention' : 'Detected search intent'}
                >
                  <Sparkles className="w-3 h-3" />
                  {info.label}
                </span>
              </div>
            );
          })()}

          {/* Author + Date — editable, persists in blog_structured.author / .published_date */}
          <div className="flex items-center flex-wrap text-sm text-gray-500 mb-6 gap-x-1" data-testid="blog-author-date-row">
            <span>{t.by}</span>
            <Editable
              as="span"
              value={blogData?.author || 'xchannels.ai'}
              onChange={(v) => updateField('author', v || 'xchannels.ai')}
              className="font-medium px-1"
              style={{ color: '#0a1628', display: 'inline-block', minWidth: '60px' }}
              testId="blog-edit-author"
            />
            <span className="mx-1">·</span>
            <Editable
              as="span"
              value={blogData?.published_date || new Date().toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US')}
              onChange={(v) => updateField('published_date', v || new Date().toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US'))}
              className="px-1"
              style={{ color: '#6b7280', display: 'inline-block', minWidth: '70px' }}
              testId="blog-edit-published-date"
            />
            {meta.reading_time_minutes ? <><span className="mx-1">·</span><span>{meta.reading_time_minutes} {t.reading_time}</span></> : null}
          </div>

          {heroImageUrl && (
            <img src={heroImageUrl} alt={blogData?.image_alt_suggestion || title}
              className="w-full aspect-video object-cover rounded-xl mb-8" loading="lazy" />
          )}

          {/* Intro — editable */}
          {(intro || true) && (
            <div className="mb-8 intro-block">
              <RichEditable value={intro} onChange={(v) => updateField('intro', v)}
                lang={lang}
                placeholder="Intro…"
                testId="blog-edit-intro" />
              <WordCharCounter text={intro} t={t} />
            </div>
          )}

          {/* TOC */}
          {sections.length > 0 && (
            <nav className="mb-8 p-5 rounded-lg" style={{ background: '#f0fdfc', borderLeft: `3px solid ${accent}` }} data-testid="blog-viewer-toc">
              <p className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: '#0a1628' }}>{t.toc}</p>
              <ul className="space-y-1.5 text-sm">
                {sections.map((s, i) => (
                  <li key={i}>
                    <a href={`#section-${i}`} onClick={(e) => { e.preventDefault(); scrollToSection(i); }}
                      style={{ color: accent }} className="hover:underline cursor-pointer" data-testid={`blog-toc-link-${i}`}>{i + 1}. {secTitle(s)}</a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Takeaways */}
          {takeaways.length > 0 && (
            <div className="mb-10 p-5 rounded-lg" style={{ background: '#f0fdfc', borderLeft: `3px solid ${accent}` }} data-testid="blog-viewer-takeaways">
              <p className="text-sm font-semibold uppercase tracking-wide mb-3 flex items-center gap-2" style={{ color: '#0a1628' }}>
                <Sparkles className="w-4 h-4" style={{ color: accent }} /> {t.takeaways}
              </p>
              <ul className="space-y-2" style={{ color: '#0a1628' }}>
                {takeaways.map((tk, i) => (
                  <li key={i} className="flex gap-2"><span className="font-bold mt-0.5" style={{ color: accent }}>→</span>
                    <Editable value={tk} onChange={(v) => updateField(`key_takeaways.${i}`, v)} className="flex-1" testId={`blog-edit-takeaway-${i}`} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sections — editable */}
          {sections.map((s, i) => (
            <section
              key={i}
              id={`section-${i}`}
              ref={(el) => { sectionRefs.current[i] = el; }}
              className="mb-10 scroll-mt-20"
            >
              <Editable as="h2" value={secTitle(s)} onChange={(v) => updateField(`sections.${i}.h2`, v)}
                className="text-2xl md:text-3xl font-bold mb-4 leading-tight" style={{ color: '#0a1628' }}
                testId={`blog-edit-section-h2-${i}`} />
              <RichEditable value={secBody(s)} onChange={(v) => updateField(`sections.${i}.content`, v)}
                lang={lang}
                testId={`blog-edit-section-content-${i}`} />
              <WordCharCounter text={secBody(s)} t={t} />
              {(s.h3_blocks || []).filter(b => b && (b.content || b.body || b.h3 || b.header)).map((b, bi) => (
                <div key={bi} className="mt-4">
                  <Editable as="h3" value={b.h3 || b.header || b.headline || ''} onChange={(v) => updateField(`sections.${i}.h3_blocks.${bi}.h3`, v)}
                    className="text-xl font-semibold mt-6 mb-3" style={{ color: '#0a1628' }} />
                  <RichEditable value={b.content || b.body || ''} onChange={(v) => updateField(`sections.${i}.h3_blocks.${bi}.content`, v)}
                    lang={lang}
                    testId={`blog-edit-section-h3-content-${i}-${bi}`} />
                </div>
              ))}
            </section>
          ))}

          {/* Conclusion */}
          {(conclusion || true) && (
            <section id="conclusion" className="mb-10 scroll-mt-20">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight" style={{ color: '#0a1628' }}>{t.conclusion}</h2>
              <RichEditable value={conclusion} onChange={(v) => updateField('conclusion', v)}
                lang={lang}
                testId="blog-edit-conclusion" />
              <WordCharCounter text={conclusion} t={t} />
            </section>
          )}

          {/* FAQ */}
          {faq.length > 0 && (
            <section id="faq" className="mb-10 scroll-mt-20" data-testid="blog-viewer-faq">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-tight" style={{ color: '#0a1628' }}>{t.faq}</h2>
              <div className="divide-y divide-gray-200 border-y border-gray-200">
                {faq.map((f, i) => {
                  const isOpen = !!openFaq[i];
                  return (
                    <div key={i} className="py-4">
                      <div className="flex items-start justify-between gap-4">
                        <Editable value={f?.question || ''} onChange={(v) => updateField(`faq.${i}.question`, v)}
                          className="flex-1 text-base md:text-lg font-semibold cursor-text"
                          style={{ color: accent }}
                          testId={`blog-edit-faq-q-${i}`} />
                        <button onClick={() => setOpenFaq(prev => ({ ...prev, [i]: !prev[i] }))} className="no-print mt-1 flex-shrink-0">
                          {isOpen ? <ChevronDown className="w-5 h-5" style={{ color: accent }} /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                        </button>
                      </div>
                      {isOpen && (
                        <div className="mt-3 pr-9">
                          <Editable value={f?.answer || ''} onChange={(v) => updateField(`faq.${i}.answer`, v)}
                            className="leading-relaxed whitespace-pre-wrap" style={{ color: '#0a1628' }}
                            testId={`blog-edit-faq-a-${i}`} />
                          <WordCharCounter text={f?.answer} t={t} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Verwendete Quellen — author-only, never printed/exported */}
          {Array.isArray(blogData?.sources) && blogData.sources.length > 0 && (
            <section className="sources-panel no-print mb-10 rounded-xl border border-dashed border-gray-300 bg-slate-50/60 p-5" data-testid="blog-viewer-sources">
              <header className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-slate-600" />
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">
                  {lang === 'de' ? 'Verwendete Quellen' : 'Sources'}
                </h3>
                <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-700">
                  {lang === 'de' ? 'Nur für Autoren' : 'Author-only'}
                </span>
                <span className="text-xs text-slate-500">· {blogData.sources.length}</span>
              </header>
              <p className="text-xs text-slate-500 mb-4">
                {lang === 'de'
                  ? 'Diese Übersicht dient der Qualitätssicherung. Sie erscheint nicht im veröffentlichten Artikel.'
                  : 'This panel is for editorial QA only and does not appear in the published article.'}
              </p>
              <ul className="space-y-3" data-testid="blog-viewer-sources-list">
                {blogData.sources.map((s, i) => {
                  const stype = s?.source_type || 'external_ai_knowledge';
                  const typeLabels = {
                    de: {
                      brand_dna:              { label: 'Brand DNA',           cls: 'bg-teal-100 text-teal-800' },
                      user_brief:             { label: 'User-Briefing',       cls: 'bg-sky-100 text-sky-800' },
                      external_verified:      { label: 'Externe Quelle',      cls: 'bg-emerald-100 text-emerald-800' },
                      external_ai_knowledge:  { label: 'KI-Wissen',           cls: 'bg-amber-100 text-amber-800' },
                    },
                    en: {
                      brand_dna:              { label: 'Brand DNA',           cls: 'bg-teal-100 text-teal-800' },
                      user_brief:             { label: 'User Brief',          cls: 'bg-sky-100 text-sky-800' },
                      external_verified:      { label: 'External Source',     cls: 'bg-emerald-100 text-emerald-800' },
                      external_ai_knowledge:  { label: 'AI Training Data',    cls: 'bg-amber-100 text-amber-800' },
                    },
                  };
                  const badge = (typeLabels[lang] || typeLabels.en)[stype] || (typeLabels[lang] || typeLabels.en).external_ai_knowledge;
                  return (
                  <li key={i} className="p-3 rounded-lg bg-white border border-slate-200" data-testid={`blog-viewer-source-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}
                                data-testid={`blog-viewer-source-type-${i}`}>
                            {badge.label}
                          </span>
                          <span className="font-semibold text-sm text-slate-900 break-words">
                            {s?.title || (lang === 'de' ? '(ohne Titel)' : '(untitled)')}
                          </span>
                        </div>
                        {s?.url ? (
                          <a href={s.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-[#1e9ead] hover:underline break-all inline-block mt-0.5"
                            data-testid={`blog-viewer-source-url-${i}`}>
                            {s.url}
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400 italic">
                            {lang === 'de' ? 'Keine URL angegeben' : 'no URL provided'}
                          </span>
                        )}
                        {s?.context && (
                          <div className="text-xs text-slate-600 mt-1.5">
                            <span className="font-medium">{lang === 'de' ? 'Kontext: ' : 'Context: '}</span>
                            {s.context}
                          </div>
                        )}
                      </div>
                      {s?.accessed_date && (
                        <div className="flex-shrink-0 text-[10px] font-mono text-slate-500 whitespace-nowrap">
                          {s.accessed_date}
                        </div>
                      )}
                    </div>
                  </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Related — single line */}
          <div className="related-block" style={{ fontSize: '13px', color: '#94A3B8', textAlign: 'center', padding: '24px 0' }}>
            {t.related_line}
          </div>

          {/* CTA */}
          <section className="cta-block mb-10 p-6 md:p-8 rounded-xl text-center" style={{ background: '#0a1628', color: '#ffffff' }}>
            <p className="text-lg md:text-xl font-semibold mb-4">{t.cta_headline}</p>
            <a href="https://xchannels.ai" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium transition-opacity hover:opacity-90"
              style={{ background: accent, color: '#ffffff' }}
              data-testid="blog-viewer-cta-btn">{t.cta_button} <ExternalLink className="w-4 h-4" /></a>
          </section>

          {/* Meta box */}
          {hasMeta && (
            <div className="meta-box mb-6 border border-gray-200 rounded-lg overflow-hidden" data-testid="blog-viewer-meta-box">
              <button onClick={() => setMetaOpen(!metaOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                <span className="text-sm font-semibold" style={{ color: accent }}>{t.meta_section}</span>
                {metaOpen ? <ChevronDown className="w-4 h-4" style={{ color: accent }} /> : <ChevronRight className="w-4 h-4" style={{ color: accent }} />}
              </button>
              {metaOpen && (
                <div className="p-4 space-y-4 text-sm bg-white">
                  <MetaEditableField label={t.meta_title_lbl} value={meta.meta_title}
                    onChange={(v) => updateField('meta.meta_title', v)}
                    counter={metaTitleLen} ok={metaTitleOk} okRange="50–60" />
                  <MetaEditableField label={t.meta_desc_lbl} value={meta.meta_description}
                    onChange={(v) => updateField('meta.meta_description', v)}
                    counter={metaDescLen} ok={metaDescOk} okRange="140–155" />
                  <MetaEditableField label={t.slug_lbl} value={meta.slug} onChange={(v) => updateField('meta.slug', v)} prefix="/blog/" mono />
                  <MetaEditableField label={t.keyword_lbl} value={meta.main_keyword} onChange={(v) => updateField('meta.main_keyword', v)} />
                  <MetaEditableField label={t.image_filename_lbl} value={blogData?.image_filename_suggestion} onChange={(v) => updateField('image_filename_suggestion', v)} mono />
                  <MetaEditableField label={t.image_alt_lbl} value={blogData?.image_alt_suggestion} onChange={(v) => updateField('image_alt_suggestion', v)} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── HTML VIEW — editable code editor ── */}
      {view === 'html' && (
        <div className="relative" data-testid="blog-viewer-html">
          {/* Sync draft when switching to HTML tab or preview data changes */}
          <HtmlEditor
            key={`html-editor-${blogData?.id || 'default'}`}
            cleanHtml={cleanHtml}
            currentBlog={blogData}
            onApply={(html) => {
              const result = htmlToBlogStructured(html, blogData);
              if (!result.ok) return { ok: false, error: result.error };
              setBlogData(result.blog);
              return { ok: true };
            }}
            onRestore={(snapshot) => {
              setBlogData(snapshot);
            }}
            onCopy={handleCopy}
            copied={copied}
            lang={lang}
            undoStack={htmlUndoStack}
            setUndoStack={setHtmlUndoStack}
          />
        </div>
      )}
    </div>
  );
});

export default BlogArticleViewer;

const MetaEditableField = ({ label, value, onChange, counter, ok, okRange, prefix = '', mono = false }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      {counter !== undefined && (
        <span className={`text-xs font-medium ${ok ? 'text-green-600' : 'text-orange-500'}`}>
          {counter} {okRange ? `(ideal ${okRange})` : ''}
        </span>
      )}
    </div>
    <div className={`flex items-center gap-1 px-3 py-2 rounded border border-gray-200 bg-gray-50 ${mono ? 'font-mono text-xs' : 'text-sm'}`}>
      {prefix && <span className="text-gray-400">{prefix}</span>}
      <div contentEditable suppressContentEditableWarning
        className="flex-1 outline-none text-gray-800"
        onBlur={(e) => onChange(e.currentTarget.innerText)}
        dangerouslySetInnerHTML={{ __html: escapeHtml(value || '') }} />
    </div>
  </div>
);
